import { ColorToken } from '@backrs_ui/common-ui/src/designSystem/colors/colors';
import { AppIconProps } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import { FeaturedIcon } from '@backrs_ui/common-ui/src/designSystem/icons/FeaturedIcon';

import { Box, Typography, Theme, Stack, SxProps } from '@mui/material';

import { InfoModal } from '../InfoModal';

type ModalSection = Readonly<{
  iconName: AppIconProps['iconName'];
  color: ColorToken;
  title: string;
  subtitle: string;
  content: string;
}>;

const BodyBox = ({ section }: { section: ModalSection }) => (
  <Box sx={bodyBoxContainerSx}>
    <FeaturedIcon
      iconName={section.iconName}
      size="lg"
      color="Colors/Base/White"
      backgroundColor={section.color}
      shape="round"
    />
    <Stack>
      <Box>
        <Typography
          component="span"
          color={(theme: Theme) => theme.backrs.colors['Gray/700']}
          variant="text-md-bold"
        >
          {section.title}
        </Typography>{' '}
        <Typography component="span" variant="text-sm-regular" sx={subtitleSx}>
          {section.subtitle}
        </Typography>
      </Box>
      <Typography
        color={(theme: Theme) => theme.backrs.colors['Gray/600']}
        variant="text-sm-regular"
      >
        {section.content}
      </Typography>
    </Stack>
  </Box>
);

const BodyContent = ({ sections }: { sections: readonly ModalSection[] }) => (
  <Box sx={bodyContentContainerSx}>
    {sections.map((section) => (
      <BodyBox key={section.title} section={section} />
    ))}
  </Box>
);

export type ModalData = Readonly<{
  title: string;
  description: string;
  modalCards: readonly ModalSection[];
}>;

const HeaderContent = ({ data }: { data: ModalData }) => (
  <Box sx={headerContentContainerSx}>
    <Typography
      color={(theme: Theme) => theme.backrs.colors['Gray/900']}
      variant="text-lg-semibold"
    >
      {data.title}
    </Typography>
    <Typography
      color={(theme: Theme) => theme.backrs.colors['Colors/Base/Black']}
      variant="text-sm-regular"
    >
      {data.description}
    </Typography>
  </Box>
);

type ChartCardModalProps = Readonly<{
  isModalOpen: boolean;
  setIsModalOpen: (open: boolean) => void;
  modalData: ModalData;
}>;

export const ChartCardModal = ({ isModalOpen, setIsModalOpen, modalData }: ChartCardModalProps) => (
  <InfoModal
    open={isModalOpen}
    onClose={() => setIsModalOpen(false)}
    headerContent={<HeaderContent data={modalData} />}
    bodyContent={<BodyContent sections={modalData.modalCards} />}
  />
);

const bodyBoxContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  padding: backrs.spacing['spacing-xl'],
  gap: backrs.spacing['spacing-lg'],
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Gray/200']}`,
});

const subtitleSx: SxProps<Theme> = ({ backrs }) => ({
  fontStyle: 'italic',
  color: backrs.colors['Gray/600'],
});

const headerContentContainerSx: SxProps<Theme> = ({ backrs }) => ({
  gap: backrs.spacing['spacing-xxs'],
});

const bodyContentContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});
